import { KPI_TEXT_SHADOW } from '@/components/ui/KpiCard';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ScanLine, MapPin, Boxes, Warehouse, X, CheckCircle2, MinusCircle, PlusCircle,
  AlertTriangle, Info, Trash2, ArrowLeftRight, type LucideIcon,
} from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Pagination } from '@/components/ui/Pagination';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { Textarea } from '@/components/form/Textarea';
import { SearchableSelect } from '@/components/form/SearchableSelect';
import { useToast } from '@/components/ui/useToast';
import { apiError } from '@/lib/apiError';
import { listLocations } from '@/api/locations';
import {
  listWarehouseStock,
  auditReconcile,
  auditApplyCorrections,
  auditAddNewSerials,
  auditMoveSerials,
  type AuditReconcileResult,
  type AuditSerial,
} from '@/api/warehouseStock';

type Scope = 'location' | 'item' | 'all';

const SCOPES: { key: Scope; label: string; icon: typeof MapPin; hint: string }[] = [
  { key: 'location', label: 'By Location', icon: MapPin, hint: 'Scan everything in one bin' },
  { key: 'item', label: 'By Item(s)', icon: Boxes, hint: 'Spot-count chosen items' },
  { key: 'all', label: 'Whole Warehouse', icon: Warehouse, hint: 'Full count — scan everything' },
];

const today = () => new Date().toISOString().slice(0, 10);

const REASONS = ['Physical count correction', 'Damaged / write-off', 'Stolen', 'Lost', 'Found', 'Data / system correction', 'Other'];

export function StockAuditPage() {
  const qc = useQueryClient();
  const toast = useToast();

  const [scope, setScope] = useState<Scope>('location');
  const [locationId, setLocationId] = useState<number | ''>('');
  const [selectedItems, setSelectedItems] = useState<{ id: number; label: string }[]>([]);
  const [itemPick, setItemPick] = useState<number | string | ''>('');

  const [scanText, setScanText] = useState('');
  const [scanned, setScanned] = useState<string[]>([]);

  const [result, setResult] = useState<AuditReconcileResult | null>(null);
  const [deductSel, setDeductSel] = useState<Set<string>>(new Set());
  const [addSel, setAddSel] = useState<Set<string>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reasonSel, setReasonSel] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [remarks, setRemarks] = useState('');
  // Add-new (unrecognised → assign item → add) state.
  const [unknownSel, setUnknownSel] = useState<Set<string>>(new Set());
  const [newItemPick, setNewItemPick] = useState<number | string | ''>('');
  const [newLocationCode, setNewLocationCode] = useState<string>('');
  const [addNewMode, setAddNewMode] = useState(false);
  // Move (on hand in another bin → this bin) state — by-location audits only.
  const [moveSel, setMoveSel] = useState<Set<string>>(new Set());
  const [moveConfirmOpen, setMoveConfirmOpen] = useState(false);
  const resultRef = useRef<HTMLDivElement>(null);

  const reasonText = reasonSel === 'Other' ? customReason.trim() : reasonSel;
  const finalReason = [reasonText, remarks.trim()].filter(Boolean).join(' — ');

  function openConfirm() {
    // Retire: 1..what the barcode holds. Restore: more than 0.
    const badRetire = [...deductSel].find((sn) => { const q = qtyOr(sn, holdsOf(result?.missing, sn)); return !(q > 0) || q > holdsOf(result?.missing, sn); });
    if (badRetire) { toast.push(`${badRetire}: retire between 1 and ${holdsOf(result?.missing, badRetire)}`, 'warning'); return; }
    const badRestore = [...addSel].find((sn) => !(qtyOr(sn, holdsOf(result?.extraFound, sn)) > 0));
    if (badRestore) { toast.push(`${badRestore}: enter a quantity greater than 0`, 'warning'); return; }
    setReasonSel('');
    setCustomReason('');
    setRemarks('');
    setAddNewMode(false);
    setConfirmOpen(true);
  }

  function openAddNewConfirm() {
    // Item + location are already chosen inline in the Unrecognised section.
    const bad = [...unknownSel].find((sn) => !(qtyOr(sn, 1) > 0));
    if (bad) { toast.push(`${bad}: enter a quantity greater than 0`, 'warning'); return; }
    setReasonSel('');
    setCustomReason('');
    setRemarks('');
    setAddNewMode(true);
    setConfirmOpen(true);
  }

  // Quantity typed per barcode (retire / restore / add-new). A serial sits in only
  // one list, so one map serves all three. Blank ⇒ the default for that list.
  const [qtyBySerial, setQtyBySerial] = useState<Record<string, string>>({});
  const setSerialQty = (sn: string, v: string) => setQtyBySerial((prev) => ({ ...prev, [sn]: v }));
  const holdsOf = (rows: AuditSerial[] | undefined, sn: string) => Number(rows?.find((r) => r.serialNo === sn)?.qty ?? 1);
  const qtyOr = (sn: string, dflt: number) => (qtyBySerial[sn] != null && qtyBySerial[sn] !== '' ? Number(qtyBySerial[sn]) : dflt);

  const toggleUnknown = (sn: string) =>
    setUnknownSel((prev) => {
      const next = new Set(prev);
      if (next.has(sn)) next.delete(sn); else next.add(sn);
      return next;
    });

  const locationsQuery = useQuery({
    queryKey: ['locations-flat'],
    queryFn: () => listLocations({}),
    enabled: scope === 'location' || (result?.extraUnknown.length ?? 0) > 0,
    staleTime: 60_000,
  });
  const locationOptions = useMemo(
    () => (locationsQuery.data ?? []).map((l) => ({ value: l.id, label: `${l.code} · ${l.name}` })),
    [locationsQuery.data],
  );
  // Code-keyed options for placing new units (backend takes a location code).
  const locationCodeOptions = useMemo(
    () => (locationsQuery.data ?? []).map((l) => ({ value: l.code, label: `${l.code} · ${l.name}` })),
    [locationsQuery.data],
  );

  const itemsQuery = useQuery({
    queryKey: ['wh-stock-all-for-audit'],
    queryFn: () => listWarehouseStock({ pageSize: 5000 }),
    enabled: scope === 'item' || (result?.extraUnknown.length ?? 0) > 0,
    staleTime: 60_000,
  });
  const allItemOptions = useMemo(
    () => (itemsQuery.data?.data ?? []).map((r) => ({ value: r.itemId, label: `${r.itemCode} · ${r.itemName}` })),
    [itemsQuery.data],
  );
  const itemOptions = useMemo(
    () => allItemOptions.filter((o) => !selectedItems.some((s) => s.id === o.value)),
    [allItemOptions, selectedItems],
  );
  const newItemLabel = allItemOptions.find((o) => o.value === newItemPick)?.label ?? '';

  // ---- scan pad ----
  function addScans(raw: string) {
    const tokens = raw.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
    if (!tokens.length) return;
    let dupes = 0;
    setScanned((prev) => {
      // Case doesn't matter for a serial: GT031844 and gt031844 are the same unit.
      const seen = new Set(prev.map((p) => p.toUpperCase()));
      const next = [...prev];
      for (const t of tokens) {
        if (seen.has(t.toUpperCase())) { dupes++; continue; }
        seen.add(t.toUpperCase());
        next.push(t);
      }
      return next;
    });
    if (dupes) toast.push(`${dupes} duplicate scan${dupes === 1 ? '' : 's'} ignored`, 'warning');
    setScanText('');
  }
  const removeScan = (sn: string) => setScanned((p) => p.filter((s) => s !== sn));
  const clearScans = () => { setScanned([]); setResult(null); };

  function addItem(v: number | string | '') {
    if (v === '') return;
    const opt = itemOptions.find((o) => o.value === v);
    if (opt) setSelectedItems((p) => [...p, { id: Number(v), label: opt.label }]);
    setItemPick('');
  }
  const removeItem = (id: number) => setSelectedItems((p) => p.filter((s) => s.id !== id));

  const scopeReady =
    scope === 'all' ||
    (scope === 'location' && locationId !== '') ||
    (scope === 'item' && selectedItems.length > 0);

  // ---- reconcile ----
  const reconcileMut = useMutation({
    mutationFn: () =>
      auditReconcile({
        scope,
        locationId: scope === 'location' ? Number(locationId) : undefined,
        itemIds: scope === 'item' ? selectedItems.map((s) => s.id) : undefined,
        scannedSerials: scanned,
      }),
    onSuccess: (r) => {
      setResult(r);
      setQtyBySerial({});
      // Missing units start unmarked — retiring stock is a deliberate choice ("Mark all" or one by one).
      setDeductSel(new Set());
      setAddSel(new Set(r.extraFound.map((s) => s.serialNo)));
      setMoveSel(new Set(r.scope === 'location' ? r.onHandElsewhere.map((s) => s.serialNo) : []));
      // For a by-location audit, default the add-new placement to the audited bin.
      if (scope === 'location') {
        const binCode = (locationsQuery.data ?? []).find((l) => l.id === Number(locationId))?.code ?? '';
        setNewLocationCode(binCode);
      }
      const misplaced = r.scope === 'location' ? r.onHandElsewhere.length : 0;
      if (r.balanced && misplaced === 0) toast.push('Physical count matches the system — nothing to adjust', 'success');
      else if (r.balanced) toast.push(`Counts match — ${misplaced} scanned unit${misplaced === 1 ? ' is' : 's are'} recorded in another location`, 'info');
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
    },
    onError: (e) => toast.push(apiError(e, 'Reconcile failed'), 'error'),
  });

  // ---- apply ----
  const applyMut = useMutation({
    mutationFn: () =>
      auditApplyCorrections({
        deductSerials: [...deductSel],
        deductQuantities: [...deductSel].map((sn) => qtyOr(sn, holdsOf(result?.missing, sn))),
        addSerials: [...addSel],
        addQuantities: [...addSel].map((sn) => qtyOr(sn, holdsOf(result?.extraFound, sn))),
        reason: `Physical audit ${today()} — ${finalReason}`,
        refDoc: `AUDIT ${today()}`,
      }),
    onSuccess: (r) => {
      toast.push(`Audit applied — ${r.deducted} retired (${r.deductedUnits ?? r.deducted} pcs), ${r.added} restored (${r.addedUnits ?? r.added} pcs)`, 'success');
      qc.invalidateQueries({ queryKey: ['warehouse-stock'] });
      qc.invalidateQueries({ queryKey: ['warehouse-stock-kpis'] });
      qc.invalidateQueries({ queryKey: ['warehouse-ledger'] });
      qc.invalidateQueries({ queryKey: ['wh-serials'] });
      setConfirmOpen(false);
      setResult(null);
      setScanned([]);
    },
    onError: (e) => { setConfirmOpen(false); toast.push(apiError(e, 'Apply failed'), 'error'); },
  });

  // ---- move (recorded in another bin → this bin) ----
  const auditedBin = (locationsQuery.data ?? []).find((l) => l.id === Number(locationId));
  const moveMut = useMutation({
    mutationFn: () => auditMoveSerials({ locationId: Number(locationId), serialNos: [...moveSel] }),
    onSuccess: (r) => {
      toast.push(`Moved ${r.moved} unit${r.moved === 1 ? '' : 's'} to ${r.location.code}${r.alreadyThere ? ` · ${r.alreadyThere} already there` : ''}`, 'success');
      qc.invalidateQueries({ queryKey: ['wh-serials'] });
      qc.invalidateQueries({ queryKey: ['warehouse-stock'] });
      qc.invalidateQueries({ queryKey: ['locations'] });
      qc.invalidateQueries({ queryKey: ['locations-flat'] });
      qc.invalidateQueries({ queryKey: ['location-occupancy'] });
      qc.invalidateQueries({ queryKey: ['location-history'] });
      setMoveConfirmOpen(false);
      // Re-check the bin: the moved units now count as matched.
      reconcileMut.mutate();
    },
    onError: (e) => { setMoveConfirmOpen(false); toast.push(apiError(e, 'Move failed'), 'error'); },
  });
  // Where the selected units are moving from, for the confirm dialog.
  const moveFrom = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of result?.onHandElsewhere ?? []) {
      if (!moveSel.has(r.serialNo)) continue;
      const k = r.location?.code ?? 'no location';
      m.set(k, (m.get(k) ?? 0) + 1);
    }
    return [...m.entries()];
  }, [result, moveSel]);
  const movePieces = (result?.onHandElsewhere ?? []).filter((r) => moveSel.has(r.serialNo)).reduce((a, r) => a + Number(r.qty), 0); // qty arrives as a decimal string
  const binMismatch = result?.scope === 'location' ? result.onHandElsewhere.length : 0;

  // ---- add new (unrecognised → assign item → add) ----
  const addNewMut = useMutation({
    mutationFn: () =>
      auditAddNewSerials({
        itemId: Number(newItemPick),
        serialNos: [...unknownSel],
        quantities: [...unknownSel].map((sn) => qtyOr(sn, 1)),
        reason: `Physical audit ${today()} — ${finalReason}`,
        refDoc: `AUDIT ${today()}`,
        locationCode: newLocationCode || undefined,
      }),
    onSuccess: (r) => {
      toast.push(`${r.added} pcs added as ${r.item.code} on ${unknownSel.size} new barcode(s)`, 'success');
      qc.invalidateQueries({ queryKey: ['warehouse-stock'] });
      qc.invalidateQueries({ queryKey: ['warehouse-stock-kpis'] });
      qc.invalidateQueries({ queryKey: ['warehouse-ledger'] });
      qc.invalidateQueries({ queryKey: ['wh-serials'] });
      // Drop the added serials from the unrecognised list so it stays accurate.
      const added = new Set(unknownSel);
      setResult((prev) => prev ? { ...prev, extraUnknown: prev.extraUnknown.filter((s) => !added.has(s)) } : prev);
      setUnknownSel(new Set());
      setNewItemPick('');
      setNewLocationCode('');
      setConfirmOpen(false);
      setAddNewMode(false);
    },
    onError: (e) => { setConfirmOpen(false); toast.push(apiError(e, 'Add failed'), 'error'); },
  });

  const toggle = (set: Set<string>, setter: (s: Set<string>) => void, sn: string) => {
    const next = new Set(set);
    if (next.has(sn)) next.delete(sn); else next.add(sn);
    setter(next);
  };
  const allOf = (rows: AuditSerial[]) => new Set(rows.map((r) => r.serialNo));
  const selectionCount = deductSel.size + addSel.size;
  // Pieces chosen per list (defaults: retire = all it holds, restore = what it last held, new = 1).
  const retirePcs = [...deductSel].reduce((a, sn) => a + qtyOr(sn, holdsOf(result?.missing, sn)), 0);
  const restorePcs = [...addSel].reduce((a, sn) => a + qtyOr(sn, holdsOf(result?.extraFound, sn)), 0);
  const newPcs = [...unknownSel].reduce((a, sn) => a + qtyOr(sn, 1), 0);

  return (
    // Natural-height content — the layout's <main> is the scroll container, so a
    // tall reconciliation scrolls the whole page with the standard scrollbar.
    <div className="flex flex-col gap-3 pb-4 sm:[&_input:not([type=checkbox])]:h-8 sm:[&_input:not([type=checkbox])]:py-1 sm:[&_input:not([type=checkbox])]:text-xs">
      <SectionHeader
        title="Stock Audit"      />

      {/* SCOPE */}
      <section className="overflow-hidden rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] backdrop-blur-xl">
        <div className="flex items-center justify-between gap-2 border-b border-[var(--border-color)] px-3 py-2.5 sm:px-5">
          <span className="text-sm font-extrabold text-[var(--text-primary)]"><span className="text-accent">1 ·</span> Audit Scope</span>
        </div>
        <div className="p-3 sm:p-4">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          {SCOPES.map((s) => {
            const on = scope === s.key;
            const Icon = s.icon;
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => { setScope(s.key); setResult(null); }}
                className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-left transition ${on ? 'border-accent bg-accent/[0.08]' : 'border-[var(--border-color)] bg-[var(--bg-soft)] hover:border-accent/50'}`}
              >
                <Icon size={16} className={on ? 'text-accent' : 'text-[var(--text-muted)]'} />
                <div>
                  <div className="text-xs font-extrabold text-[var(--text-primary)]">{s.label}</div>
                  <div className="text-[10px] text-[var(--text-muted)]">{s.hint}</div>
                </div>
              </button>
            );
          })}
        </div>

        {scope === 'location' && (
          <div className="mt-3 max-w-md">
            <SearchableSelect
              options={locationOptions}
              value={locationId}
              onChange={(v) => { setLocationId(v === '' ? '' : Number(v)); setResult(null); }}
              placeholder="Search / scan a bin…"
              leftIcon={<MapPin size={15} />}
            />
          </div>
        )}

        {scope === 'item' && (
          <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-start">
            <div className="w-full shrink-0 sm:max-w-xs">
              <SearchableSelect
                options={itemOptions}
                value={itemPick}
                onChange={addItem}
                placeholder={itemsQuery.isLoading ? 'Loading items…' : 'Add an item to the count…'}
                leftIcon={<Boxes size={15} />}
              />
            </div>
            {selectedItems.length > 0 && (
              <div className="flex flex-1 flex-wrap gap-1.5 sm:pt-1.5">
                {selectedItems.map((it) => (
                  <span key={it.id} className="inline-flex items-center gap-1 rounded-full bg-accent/10 px-2 py-0.5 text-xs text-accent">
                    {it.label}
                    <button type="button" onClick={() => { removeItem(it.id); setResult(null); }} className="hover:text-danger">
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        )}

        {scope === 'all' && (
          <div className="mt-3 flex items-center gap-2 rounded-md bg-warning/10 px-3 py-2 text-xs font-semibold text-warning">
            <AlertTriangle size={15} />
            Whole-warehouse count: every on-hand serial you don&apos;t scan will be flagged as missing. Only use this once you have physically counted everything.
          </div>
        )}
        </div>
      </section>

      {/* SCAN PAD */}
      <section className="overflow-hidden rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] backdrop-blur-xl">
        <div className="flex items-center justify-between gap-2 border-b border-[var(--border-color)] px-3 py-2.5 sm:px-5">
          <span className="text-sm font-extrabold text-[var(--text-primary)]"><span className="text-accent">2 ·</span> Scan the Physical Stock</span>
          <span className="text-xs font-bold text-[var(--text-secondary)]">{scanned.length} scanned</span>
        </div>
        <div className="p-3 sm:p-4">
        <div className="relative">
          <ScanLine size={15} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <Input
            autoFocus
            value={scanText}
            onChange={(e) => setScanText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addScans(scanText); } }}
            placeholder="Scan a serial and press Enter — or paste many, separated by spaces / new lines"
            className="!pl-8 font-mono"
            autoComplete="off"
          />
        </div>

        {scanned.length > 0 && (
          <>
            {/* Every scanned serial is shown (no cap) so a whole-warehouse count
                is fully visible; the fixed-height box scrolls to review them all. */}
            <div className="mt-2 flex max-h-48 flex-wrap gap-1 overflow-y-auto rounded-md border border-[var(--border-soft)] bg-[var(--bg-soft)] p-2 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[var(--border-color)]">
              {scanned.map((sn) => (
                <span key={sn} className="inline-flex items-center gap-1 rounded bg-white/[0.06] px-1.5 py-0.5 font-mono text-[10px] text-[var(--text-secondary)]">
                  {sn}
                  <button type="button" onClick={() => removeScan(sn)} className="hover:text-danger"><X size={10} /></button>
                </span>
              ))}
            </div>
            <button type="button" onClick={clearScans} className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-[var(--text-muted)] hover:text-danger">
              <Trash2 size={12} /> Clear all
            </button>
          </>
        )}

        <div className="mt-3">
          <Button
            variant="primary"
            size="sm"
            onClick={() => reconcileMut.mutate()}
            disabled={reconcileMut.isPending || scanned.length === 0 || !scopeReady}
            title={!scopeReady ? 'Choose a scope first' : scanned.length === 0 ? 'Scan at least one serial' : ''}
          >
            {reconcileMut.isPending ? 'Reconciling…' : 'Reconcile against system'}
          </Button>
        </div>
        </div>
      </section>

      {/* RESULT */}
      {result && (
        <section ref={resultRef} className="overflow-hidden rounded-card border border-[var(--border-color)] bg-[var(--card-bg)] backdrop-blur-xl">
          <div className="flex items-center justify-between gap-2 border-b border-[var(--border-color)] px-3 py-2.5 sm:px-5">
            <span className="text-sm font-extrabold text-[var(--text-primary)]"><span className="text-accent">3 ·</span> Reconciliation</span>
            {result.balanced && binMismatch === 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-success/15 px-2 py-0.5 text-[11px] font-bold text-success">
                <CheckCircle2 size={12} /> Balanced
              </span>
            )}
          </div>
          <div className="p-3 sm:p-4">

          {/* summary chips */}
          <div className="mb-3 flex gap-1.5">
            <StatKpi label="Scanned" value={result.scannedCount} tone="primary" Icon={ScanLine} />
            <StatKpi label="Matched" value={result.matchedCount} sub={`${result.matchedUnits} pcs`} tone="success" Icon={CheckCircle2} />
            <StatKpi label="Missing" value={result.missing.length} sub={`${result.missingUnits} pcs`} tone="danger" Icon={MinusCircle} />
            <StatKpi label="Found" value={result.extraFound.length} sub={`${result.extraFoundUnits} pcs`} tone="success" Icon={PlusCircle} />
            <StatKpi label="Unknown" value={result.extraUnknown.length} tone="warning" Icon={AlertTriangle} />
          </div>

          {result.balanced && binMismatch === 0 && (
            <div className="flex items-center gap-2 rounded-md bg-success/10 px-3 py-2 text-sm font-bold text-success">
              <CheckCircle2 size={18} /> Physical count matches the system — nothing to adjust.
            </div>
          )}

          {/* MISSING → retire */}
          {result.missing.length > 0 && (
            <SerialSection
              tone="danger"
              icon={MinusCircle}
              title={`Missing — on system, not scanned (retire ${deductSel.size}/${result.missing.length})`}
              rows={result.missing}
              selected={deductSel}
              qtyOf={(sn) => qtyBySerial[sn]}
              onQty={setSerialQty}
              capAtHolds
              onToggle={(sn) => toggle(deductSel, setDeductSel, sn)}
              onAll={() => setDeductSel(deductSel.size === result.missing.length ? new Set() : allOf(result.missing))}
            />
          )}

          {/* FOUND → add back */}
          {result.extraFound.length > 0 && (
            <SerialSection
              tone="success"
              icon={PlusCircle}
              title={`Found — scanned, not on hand (restore ${addSel.size}/${result.extraFound.length})`}
              rows={result.extraFound}
              selected={addSel}
              qtyOf={(sn) => qtyBySerial[sn]}
              onQty={setSerialQty}
              onToggle={(sn) => toggle(addSel, setAddSel, sn)}
              onAll={() => setAddSel(addSel.size === result.extraFound.length ? new Set() : allOf(result.extraFound))}
              showStatus
            />
          )}

          {/* UNKNOWN → assign an item and add as new stock */}
          {result.extraUnknown.length > 0 && (
            <div className="mt-3">
              <div className="mb-1.5 flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-xs font-bold text-warning">
                  <AlertTriangle size={14} /> Unrecognised scans — assign an item to add ({unknownSel.size}/{result.extraUnknown.length})
                </div>
                <button
                  type="button"
                  className="text-[11px] text-accent hover:underline"
                  onClick={() => setUnknownSel(unknownSel.size === result.extraUnknown.length ? new Set() : new Set(result.extraUnknown))}
                >
                  {unknownSel.size === result.extraUnknown.length ? 'Clear all' : 'Select all'}
                </button>
              </div>
              <div className="flex max-h-40 flex-col gap-1 overflow-y-auto rounded-md border border-[var(--border-soft)] bg-[var(--bg-soft)] p-2">
                {result.extraUnknown.map((sn) => {
                  const on = unknownSel.has(sn);
                  return (
                    <label key={sn} className={`flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-xs transition ${on ? 'bg-warning/[0.12] ring-1 ring-warning/40' : 'hover:bg-white/[0.05]'}`}>
                      <input type="checkbox" checked={on} onChange={() => toggleUnknown(sn)} className="accent-warning" />
                      <span className="font-mono text-[var(--text-primary)]">{sn}</span>
                      {on && (
                        <span className="ml-auto flex shrink-0 items-center gap-1" onClick={(e) => e.preventDefault()}>
                          <input
                            type="number" min={0} step="any" value={qtyBySerial[sn] ?? '1'}
                            onChange={(e) => setSerialQty(sn, e.target.value)}
                            className="w-14 rounded border border-warning/40 bg-[var(--input-bg)] px-1.5 py-0.5 text-right text-xs font-semibold text-[var(--text-primary)]"
                            title="Pieces this barcode holds"
                          />
                          <span className="text-[10px] text-[var(--text-muted)]">pcs</span>
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
              {/* Assign an item + a location, then add */}
              <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
                <div className="w-full sm:max-w-xs">
                  <SearchableSelect
                    options={allItemOptions}
                    value={newItemPick}
                    onChange={(v) => setNewItemPick(v === '' ? '' : Number(v))}
                    placeholder={itemsQuery.isLoading ? 'Loading items…' : 'Assign these to an item…'}
                    leftIcon={<Boxes size={15} />}
                  />
                </div>
                <div className="w-full sm:max-w-xs">
                  <SearchableSelect
                    options={locationCodeOptions}
                    value={newLocationCode}
                    onChange={(v) => setNewLocationCode(v === '' ? '' : String(v))}
                    placeholder="Location to keep — search / scan a bin…"
                    leftIcon={<MapPin size={15} />}
                  />
                </div>
                <Button
                  variant="primary"
                  onClick={openAddNewConfirm}
                  disabled={unknownSel.size === 0 || newItemPick === '' || newLocationCode === ''}
                  title={unknownSel.size === 0 ? 'Select barcodes first' : newItemPick === '' ? 'Pick an item' : newLocationCode === '' ? 'Pick a location to keep them' : ''}
                >
                  {`Add ${unknownSel.size} (${newPcs} pcs) as new stock`}
                </Button>
              </div>
              <div className="mt-1 text-[11px] text-[var(--text-muted)]">
                Pick the item and the bin these barcodes go to, then add — each becomes a new on-hand unit (valued at the item&apos;s purchase rate). For a proper purchase trail, use a GRN instead.
              </div>
            </div>
          )}

          {/* ON HAND ELSEWHERE (by-location audit) → move into this bin */}
          {binMismatch > 0 && (
            <>
              <SerialSection
                tone="accent"
                icon={ArrowLeftRight}
                title={`In another location — scanned here (move ${moveSel.size}/${binMismatch})`}
                rows={result.onHandElsewhere}
                selected={moveSel}
                onToggle={(sn) => toggle(moveSel, setMoveSel, sn)}
                onAll={() => setMoveSel(moveSel.size === binMismatch ? new Set() : allOf(result.onHandElsewhere))}
              />
              <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <span className="text-[11px] text-[var(--text-muted)]">
                  These units are physically in {auditedBin?.code ?? 'this location'} but recorded in another bin. Moving them only changes their location — stock quantity and the ledger stay the same.
                </span>
                <Button
                  variant="primary"
                  size="sm"
                  icon={<ArrowLeftRight size={13} />}
                  className="shrink-0 justify-center"
                  onClick={() => setMoveConfirmOpen(true)}
                  disabled={moveSel.size === 0 || moveMut.isPending}
                >
                  {`Move ${moveSel.size} to ${auditedBin?.code ?? 'this location'}`}
                </Button>
              </div>
            </>
          )}

          {/* ON HAND ELSEWHERE (item / whole-warehouse audit) → info only */}
          {result.scope !== 'location' && result.onHandElsewhere.length > 0 && (
            <div className="mt-3">
              <div className="mb-1.5 flex items-center gap-1.5 text-xs font-bold text-[var(--text-secondary)]">
                <Info size={14} /> On hand, but outside this scope ({result.onHandElsewhere.length})
              </div>
              <div className="flex max-h-32 flex-col gap-1 overflow-y-auto rounded-md border border-[var(--border-soft)] bg-[var(--bg-soft)] p-2">
                {result.onHandElsewhere.map((s) => (
                  <div key={s.serialNo} className="flex items-center gap-2 text-[11px]">
                    <span className="font-mono text-[var(--text-secondary)]">{s.serialNo}</span>
                    <span className="text-[var(--text-muted)]">{s.item.code}</span>
                    {s.location && <span className="ml-auto text-[var(--text-muted)]">{s.location.code}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* APPLY */}
          {(result.missing.length > 0 || result.extraFound.length > 0) && (
            <div className="mt-4 flex items-center justify-end gap-3 border-t border-[var(--border-soft)] pt-3">
              <span className="text-[11px] text-[var(--text-muted)]">
                Logged as “Physical audit {today()}”
              </span>
              <Button
                variant="primary"
                onClick={openConfirm}
                disabled={applyMut.isPending || selectionCount === 0}
              >
                {applyMut.isPending
                  ? 'Applying…'
                  : `Apply — retire ${deductSel.size} (${retirePcs} pcs), restore ${addSel.size} (${restorePcs} pcs)`}
              </Button>
            </div>
          )}
          </div>
        </section>
      )}

      {/* Confirm the move — names the target bin and where the units come from. */}
      <Modal
        open={moveConfirmOpen}
        onClose={() => { if (!moveMut.isPending) setMoveConfirmOpen(false); }}
        title={`Move units to ${auditedBin?.code ?? 'this location'}`}
        size="sm"
        footer={
          <>
            <Button variant="glass" onClick={() => setMoveConfirmOpen(false)} disabled={moveMut.isPending}>Cancel</Button>
            <Button variant="primary" onClick={() => moveMut.mutate()} disabled={moveMut.isPending || moveSel.size === 0}>
              {moveMut.isPending ? 'Moving…' : `Move ${moveSel.size}`}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3 text-sm text-[var(--text-secondary)]">
          <div className="flex items-center gap-2 rounded-md bg-accent/10 px-3 py-2 font-bold text-accent">
            <ArrowLeftRight size={16} /> {moveSel.size} unit{moveSel.size === 1 ? '' : 's'}{movePieces !== moveSel.size ? ` (${movePieces} pcs)` : ''} → {auditedBin ? `${auditedBin.code} · ${auditedBin.name}` : 'this location'}
          </div>
          {moveFrom.length > 0 && (
            <div className="text-xs">
              From: {moveFrom.map(([code, n]) => `${code} ×${n}`).join(', ')}
            </div>
          )}
          <p className="text-xs text-[var(--text-muted)]">Only the location changes. Stock quantity and the warehouse ledger are not affected, and each unit&apos;s location history records the move.</p>
        </div>
      </Modal>

      {/* Confirm before committing — guards against a misclick on Apply. */}
      <Modal
        open={confirmOpen}
        onClose={() => { if (!applyMut.isPending && !addNewMut.isPending) setConfirmOpen(false); }}
        title={addNewMode ? 'Add new stock from audit' : 'Confirm audit corrections'}
        size="md"
        footer={
          <>
            <Button variant="glass" onClick={() => setConfirmOpen(false)} disabled={applyMut.isPending || addNewMut.isPending}>Cancel</Button>
            {addNewMode ? (
              <Button
                variant="primary"
                onClick={() => addNewMut.mutate()}
                disabled={addNewMut.isPending || !reasonText}
                title={!reasonText ? 'Select a reason first' : ''}
              >
                {addNewMut.isPending ? 'Adding…' : `Add ${unknownSel.size} as new`}
              </Button>
            ) : (
              <Button
                variant={deductSel.size > 0 ? 'danger' : 'primary'}
                onClick={() => applyMut.mutate()}
                disabled={applyMut.isPending || !reasonText}
                title={!reasonText ? 'Select a reason first' : ''}
              >
                {applyMut.isPending ? 'Applying…' : 'Confirm & apply'}
              </Button>
            )}
          </>
        }
      >
        <div className="flex flex-col gap-3">
          {addNewMode ? (
            <>
              <p className="text-sm text-[var(--text-secondary)]">You&apos;re about to add brand-new units to warehouse stock from this audit:</p>
              <div className="flex items-center gap-2 rounded-md bg-success/10 px-3 py-2 text-sm font-bold text-success">
                <PlusCircle size={16} /> Add {unknownSel.size} barcode{unknownSel.size === 1 ? '' : 's'} · {newPcs} pcs of {newItemLabel || 'the selected item'} @ {newLocationCode || 'the selected bin'}
              </div>
              <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/[0.06] px-3 py-2 text-xs text-warning">
                <AlertTriangle size={15} className="mt-0.5 flex-shrink-0" />
                <span>These barcodes will be created as new on-hand units, valued at the item&apos;s purchase rate. There&apos;s no vendor/purchase trail — for that, cancel and use a GRN instead.</span>
              </div>
            </>
          ) : (
          <>
          <p className="text-sm text-[var(--text-secondary)]">You&apos;re about to change warehouse stock from this physical audit:</p>
          <div className="flex flex-col gap-2">
            {deductSel.size > 0 && (
              <div className="flex items-center gap-2 rounded-md bg-danger/10 px-3 py-2 text-sm font-bold text-danger">
                <MinusCircle size={16} /> Retire {deductSel.size} barcode{deductSel.size === 1 ? '' : 's'} · {retirePcs} pcs — removed from on-hand stock
              </div>
            )}
            {addSel.size > 0 && (
              <div className="flex items-center gap-2 rounded-md bg-success/10 px-3 py-2 text-sm font-bold text-success">
                <PlusCircle size={16} /> Restore {addSel.size} barcode{addSel.size === 1 ? '' : 's'} · {restorePcs} pcs — returned to on-hand stock
              </div>
            )}
          </div>
          {deductSel.size >= 10 && (
            <div className="flex items-start gap-2 rounded-md border border-danger/40 bg-danger/[0.06] px-3 py-2 text-xs text-danger">
              <AlertTriangle size={15} className="mt-0.5 flex-shrink-0" />
              <span>This retires <strong>{deductSel.size} serial(s) / {(result?.missing ?? []).filter((r) => deductSel.has(r.serialNo)).reduce((s, r) => s + Number(r.qty), 0)} pcs</strong> at once. Please double-check the physical count is complete before confirming — this reduces stock and can&apos;t be undone from here.</span>
            </div>
          )}
          </>
          )}

          {/* Reason is required and recorded on every ledger + audit entry. */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-1">
              <label className="text-[11px] font-bold uppercase tracking-wide text-[var(--text-muted)]">Reason <span className="text-danger">*</span></label>
              <Select value={reasonSel} onChange={(e) => setReasonSel(e.target.value)} autoFocus>
                <option value="">— Select a reason —</option>
                {REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </Select>
              {reasonSel === 'Other' && (
                <Input autoFocus className="mt-1" placeholder="Type the reason for this correction" value={customReason} onChange={(e) => setCustomReason(e.target.value)} />
              )}
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[11px] font-bold uppercase tracking-wide text-[var(--text-muted)]">Remarks (optional)</label>
              <Textarea rows={2} placeholder="e.g. cycle count on 2026-09-19, verified by store manager…" value={remarks} onChange={(e) => setRemarks(e.target.value)} />
            </div>
          </div>

          <p className="text-[11px] text-[var(--text-muted)]">Recorded as “Physical audit {today()}{reasonText ? ` — ${finalReason}` : ''}” on the stock ledger and audit trail.</p>
        </div>
      </Modal>
    </div>
  );
}

const STAT_ACCENT: Record<'primary' | 'success' | 'danger' | 'warning', { color: string; bg: string }> = {
  primary: { color: '#3b82f6', bg: 'rgba(59,130,246,0.10)' },
  success: { color: '#10b981', bg: 'rgba(16,185,129,0.10)' },
  danger:  { color: '#ef4444', bg: 'rgba(239,68,68,0.10)' },
  warning: { color: '#f59e0b', bg: 'rgba(245,158,11,0.10)' },
};

/** ERP-theme KPI tile (matches Warehouse Stock KPIs): tinted surface, faded
 *  corner icon, uppercase label above, big value below. */
function StatKpi({ label, value, tone, Icon, sub }: { label: string; value: number; tone: 'primary' | 'success' | 'danger' | 'warning'; Icon: LucideIcon; sub?: string }) {
  const { color } = STAT_ACCENT[tone];
  return (
    <div
      className="relative flex-1 overflow-hidden rounded-lg p-2 text-white shadow-sm sm:rounded-xl sm:p-3"
      style={{ background: color, textShadow: KPI_TEXT_SHADOW }}
    >
      <Icon className="absolute -bottom-2 -right-2 h-12 w-12 sm:h-14 sm:w-14" style={{ color: '#fff', opacity: 0.18 }} strokeWidth={1.25} />
      <div className="relative">
        <div className="text-[9px] font-extrabold uppercase tracking-widest text-white/90 sm:text-[10px]">{label}</div>
        <div className="mt-0.5 text-lg font-extrabold tabular-nums sm:text-2xl">{value}</div>
        {sub && <div className="text-[9px] font-bold tabular-nums text-white/85">{sub}</div>}
      </div>
    </div>
  );
}

function SerialSection({
  tone, icon: Icon, title, rows, selected, onToggle, onAll, showStatus, qtyOf, onQty, capAtHolds,
}: {
  tone: 'danger' | 'success' | 'accent';
  icon: typeof MinusCircle;
  title: string;
  rows: AuditSerial[];
  selected: Set<string>;
  onToggle: (sn: string) => void;
  onAll: () => void;
  showStatus?: boolean;
  /** Qty box on ticked rows: current text (blank = default) and setter. */
  qtyOf?: (sn: string) => string | undefined;
  onQty?: (sn: string, v: string) => void;
  /** Retire can't take more than the serial holds. */
  capAtHolds?: boolean;
}) {
  const accent = tone === 'danger' ? 'text-danger' : tone === 'accent' ? 'text-accent' : 'text-success';
  const rowOn = tone === 'danger' ? 'bg-danger/[0.08] ring-1 ring-danger/40' : tone === 'accent' ? 'bg-accent/[0.08] ring-1 ring-accent/40' : 'bg-success/[0.08] ring-1 ring-success/40';

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const total = rows.length;
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  const pageSafe = Math.min(page, lastPage);
  useEffect(() => { setPage(1); }, [total]);
  const paged = rows.slice((pageSafe - 1) * pageSize, pageSafe * pageSize);

  return (
    <div className="mt-3">
      <div className="mb-1.5 flex items-center justify-between">
        <div className={`flex items-center gap-1.5 text-xs font-bold ${accent}`}>
          <Icon size={14} /> {title}
        </div>
        <button
          type="button"
          onClick={onAll}
          className="inline-flex items-center rounded-md border border-[var(--border-color)] bg-[var(--card-bg)] px-2.5 py-0.5 text-[11px] font-bold text-accent shadow-sm transition hover:border-accent"
        >
          {selected.size === rows.length ? 'Clear all' : `Mark all (${rows.length})`}
        </button>
      </div>
      <div className="overflow-hidden rounded-md border border-[var(--border-soft)] bg-[var(--bg-soft)]">
        <div className="flex max-h-72 flex-col gap-1 overflow-y-auto p-2 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[var(--border-color)]">
          {paged.map((s) => {
            const on = selected.has(s.serialNo);
            return (
              <label key={s.serialNo} className={`flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-xs transition ${on ? rowOn : 'hover:bg-white/[0.05]'}`}>
                <input type="checkbox" checked={on} onChange={() => onToggle(s.serialNo)} className={tone === 'danger' ? 'accent-danger' : tone === 'accent' ? 'accent-accent' : 'accent-success'} />
                <span className="font-mono text-[var(--text-primary)]">{s.serialNo}</span>
                <span className="text-[10px] text-[var(--text-muted)]">{s.item.code}</span>
                {s.qty > 1 && <span className="rounded bg-amber-500/15 px-1 py-0.5 text-[9px] font-bold text-amber-600" title="Box label — this serial carries multiple pieces">×{s.qty} pcs</span>}
                {showStatus && <span className="rounded bg-white/10 px-1 py-0.5 text-[9px] uppercase text-[var(--text-muted)]">{s.status}</span>}
                {s.location && <span className="ml-auto text-[10px] text-[var(--text-muted)]">{s.location.code}</span>}
                {on && onQty && (
                  <span className={`flex shrink-0 items-center gap-1 ${s.location ? '' : 'ml-auto'}`} onClick={(e) => e.preventDefault()}>
                    <input
                      type="number" min={0} step="any" {...(capAtHolds ? { max: Number(s.qty) } : {})}
                      value={qtyOf?.(s.serialNo) ?? String(Number(s.qty))}
                      onChange={(e) => onQty(s.serialNo, e.target.value)}
                      className="w-14 rounded border border-[var(--border-color)] bg-[var(--input-bg)] px-1.5 py-0.5 text-right text-xs font-semibold text-[var(--text-primary)]"
                      title={capAtHolds ? `Pieces to retire (max ${Number(s.qty)})` : 'Pieces this barcode holds'}
                    />
                    <span className="text-[10px] text-[var(--text-muted)]">{capAtHolds ? `/ ${Number(s.qty)}` : 'pcs'}</span>
                  </span>
                )}
              </label>
            );
          })}
        </div>
        {total > pageSize && (
          <div className="border-t border-[var(--border-soft)] px-2 py-1.5">
            <Pagination
              page={pageSafe}
              pageSize={pageSize}
              total={total}
              setPage={setPage}
              setPageSize={setPageSize}
              pageSizeOptions={[10, 25, 50, 100]}
            />
          </div>
        )}
      </div>
    </div>
  );
}
